import { appLocale, systemLib } from 'shared';

import type { SearchPageOpenParams } from '../analytics';
import { extractAnalyticsDataLib } from '../analytics-data-extraction';
import {
  collectPropertiesUpToEvent,
  AnalyticsEventName,
} from '../analytics-events-storage';
import { sendAcceleraEvent, normalizePhone } from './lib';
import { unixToDateString } from './convert-to-accelera-format';

/**
 * serp_page_opened - Событие открытия страницы результатов поиска авиабилетов (SERP)
 */
export const serpPageOpened = (params: SearchPageOpenParams) => {
  // Need dict and searchParams to extract city data
  if (!params.dict || !params.searchParams?.dep || !params.searchParams?.arr) {
    return;
  }

  const currentLocale = appLocale.lib.getAppLocale();

  // Reuse existing extraction logic from analytics-data-extraction
  const baseData = extractAnalyticsDataLib.searchPageOpen(params);

  // Create new object with all data
  const mappedData: Record<string, unknown> = {
    ...baseData,
  };

  // Add error if present
  if (params.error) {
    mappedData.error = params.error;
  }

  // Add search_status and sources
  mappedData.search_status = params.success ? 'success' : 'error';
  mappedData.serp_open_source = params.source;
  mappedData.search_click_source = params.source;

  // When coming from main page or price calendar, use collected properties from search_button_click
  // (same as bloomreach does) to get correct city data
  // Price calendar is a continuation of the same search with different dates
  // When coming from other sources (hot_offers, special_offers), extract city data from dict
  if (params.source === 'main_page' || params.source === 'price_calendar') {
    const collectedProps = collectPropertiesUpToEvent(
      AnalyticsEventName.SERP_PAGE_OPEN,
    );
    Object.assign(mappedData, collectedProps);

    // Convert dates from Unix (bloomreach format) to yyyy-mm-dd (accelera format)
    if (typeof mappedData.date_from === 'number') {
      mappedData.date_from = unixToDateString(mappedData.date_from);
    }
    if (typeof mappedData.date_to === 'number') {
      mappedData.date_to = unixToDateString(mappedData.date_to);
    }

    // For price_calendar, override dates with the new selected dates
    if (params.source === 'price_calendar') {
      extractAnalyticsDataLib.enrichEventWithDatesAndSearchMode(
        mappedData,
        params,
      );
    }
  } else {
    extractAnalyticsDataLib.enrichEventWithCityData(mappedData, params);
    extractAnalyticsDataLib.enrichEventWithPassengerData(mappedData, params);
    extractAnalyticsDataLib.enrichEventWithCabinClass(mappedData, params);
    extractAnalyticsDataLib.enrichEventWithDatesAndSearchMode(
      mappedData,
      params,
    );
    extractAnalyticsDataLib.enrichEventWithTravelType(mappedData, params);
  }

  // Calculate min price if offers exist
  if (params.offers && params.offers.length > 0) {
    const minPrice = Math.min(
      ...params.offers.map(o => Number(o.price.amount)),
    );
    if (minPrice !== Infinity) {
      mappedData.min_offer_price = minPrice;
    }
  }

  // Add Accelera-specific fields
  mappedData.phone = normalizePhone(params.phone);
  mappedData.platform = systemLib.getMobileOperatingSystem();
  mappedData.language = currentLocale;

  // Clean up unused fields (keep only what's needed for Accelera)
  delete mappedData.status;
  delete mappedData.source;
  delete mappedData.search_time;
  delete mappedData.segment_filter_on;
  delete mappedData.partner_filter;
  delete mappedData.calendar_filter_on;
  delete mappedData.business_in_economy_count;

  sendAcceleraEvent('serp_page_opened', mappedData);
};
